import { StandardEvents } from '@shopify/events';

/**
 * Presentation layer for the native Horizon 4.1.5 variant picker.
 * Every instance keeps its own listeners and delegates selection to the
 * native radio/select controls rendered by variant-main-picker.
 */
class GnBlockVariantPicker extends HTMLElement {
  #root;
  #section;
  #onClick;
  #onKeydown;
  #onDocumentKeydown;
  #onNativeChange;
  #onProductSelect;
  #generation = 0;

  connectedCallback() {
    this.#root = this.querySelector('[data-vc]');
    if (!this.#root) return;

    this.#section = this.closest('.shopify-section, dialog') || document;
    this.#onClick = (event) => this.#click(event);
    this.#onKeydown = (event) => {
      if (event.key === 'Escape') this.#closeMenus();
    };
    this.#onDocumentKeydown = (event) => {
      if (event.key === 'Escape') this.#closeMenus();
    };
    this.#onNativeChange = () => this.#refresh();
    this.#onProductSelect = (event) => this.#productSelect(event);

    this.addEventListener('keydown', this.#onKeydown);
    this.#section.addEventListener('change', this.#onNativeChange);
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.removeEventListener('keydown', this.#onKeydown);
    this.#section?.removeEventListener('change', this.#onNativeChange);
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#closeMenus();
  }

  #data() {
    try {
      return JSON.parse(this.#root.querySelector('[data-vc-data]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  #nativeGroups() {
    const native = this.#root.querySelector('.vc__native');
    if (!native) return [];

    const groups = [];
    const radios = [...native.querySelectorAll('input[type="radio"]')];
    const seenNames = new Set();

    radios.forEach((input) => {
      if (seenNames.has(input.name)) return;
      seenNames.add(input.name);
      groups.push({
        element: input,
        type: 'radio',
        inputs: radios.filter((candidate) => candidate.name === input.name),
      });
    });

    native.querySelectorAll('select').forEach((select) => {
      groups.push({ element: select, type: 'select', select });
    });

    return groups.sort((first, second) => {
      const position = first.element.compareDocumentPosition(second.element);
      return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
  }

  #selection() {
    const values = [];

    this.#nativeGroups().forEach((group, index) => {
      values[index] = group.type === 'radio'
        ? group.inputs.find((input) => input.checked)?.value
        : group.select.selectedOptions?.[0]?.value ?? group.select.value;
    });

    if (!values.length) {
      try {
        return JSON.parse(this.#root.dataset.vcSelection || '[]');
      } catch {
        return [];
      }
    }

    return values;
  }

  #find(list, selection, index, value) {
    return list.find((variant) => (
      variant.options?.[index] === value
      && variant.options.every((option, optionIndex) => (
        optionIndex === index || !selection[optionIndex] || option === selection[optionIndex]
      ))
    )) || list.find((variant) => variant.options?.[index] === value);
  }

  #available(list, selection, index, value) {
    return list.some((variant) => (
      variant.options?.[index] === value
      && variant.options.every((option, optionIndex) => (
        optionIndex === index || !selection[optionIndex] || option === selection[optionIndex]
      ))
      && variant.available
    ));
  }

  #paint(node, variant) {
    if (!node || !variant) return;

    if (variant.price && typeof variant.price === 'object') {
      variant = {
        ...variant,
        price: variant.price.amount,
        compare: variant.compareAtPrice?.amount,
      };
    }

    const price = node.querySelector('[data-vc-price]');
    const compare = node.querySelector('[data-vc-compare]');

    if (price && variant.price != null) {
      price.textContent = variant.price;
      price.classList.toggle('is-sale', Boolean(variant.compare));
    }

    if (compare) {
      compare.textContent = variant.compare || '';
      compare.hidden = !variant.compare;
    }
  }

  #refresh(variant) {
    const list = this.#data();
    const selection = variant?.selectedOptions?.length
      ? variant.selectedOptions.map((option) => option.value)
      : this.#selection();

    this.#root.dataset.vcSelection = JSON.stringify(selection);

    this.#root.querySelectorAll('[data-vc-select]').forEach((field) => {
      const index = Number(field.dataset.vcIndex);
      const value = selection[index];
      const label = field.querySelector('[data-vc-current-label]');

      if (label && value) label.textContent = value;
      this.#paint(field.querySelector('[data-vc-trigger]'), this.#find(list, selection, index, value));

      field.querySelectorAll('[data-vc-option]').forEach((item) => {
        const optionValue = item.dataset.vcValue;
        const match = this.#find(list, selection, index, optionValue);
        const selected = optionValue === value;

        item.classList.toggle('is-selected', selected);
        item.classList.toggle('is-unavailable', !this.#available(list, selection, index, optionValue));
        item.setAttribute('aria-selected', String(selected));
        item.setAttribute('aria-checked', String(selected));
        this.#paint(item, match);
      });
    });

    this.#root.querySelectorAll('[data-vc-card]').forEach((card) => {
      const primaryIndex = Number(this.#root.dataset.primaryIndex);
      const primaryValue = card.dataset.vcCard;
      const cardSelection = [...selection];
      const selected = primaryValue === selection[primaryIndex];

      cardSelection[primaryIndex] = primaryValue;
      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-unavailable', !this.#available(list, selection, primaryIndex, primaryValue));
      card.querySelector('.vc__row')?.setAttribute('aria-checked', String(selected));
      this.#paint(card.querySelector('.vc__row'), this.#find(list, cardSelection, primaryIndex, primaryValue));

      card.querySelectorAll('.vc__sub').forEach((sub) => {
        const secondaryIndex = Number(sub.dataset.vcIndex);
        const secondaryValue = sub.dataset.vcValue;
        const subSelected = secondaryValue === selection[secondaryIndex] && selected;

        sub.classList.toggle('is-selected', subSelected);
        sub.classList.toggle('is-unavailable', !this.#available(list, cardSelection, secondaryIndex, secondaryValue));
        sub.setAttribute('aria-checked', String(subSelected));
      });
    });

    this.#root.querySelectorAll('[data-vc-badge]').forEach((badge) => {
      const value = selection[Number(badge.dataset.vcBadgeIndex)];
      if (value) badge.textContent = `${this.#root.dataset.badgePrefix || ''} ${value}`.trim();
    });
  }

  #native(index, value) {
    const groups = this.#nativeGroups();
    const group = groups[index] || groups.find((candidate) => (
      candidate.type === 'radio'
        ? candidate.inputs.some((input) => input.value === value)
        : [...candidate.select.options].some((option) => option.value === value)
    ));

    if (!group) return;

    if (group.type === 'select') {
      group.select.value = value;
      group.select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const input = group.inputs.find((candidate) => candidate.value === value);
    if (input && !input.checked) input.click();
  }

  #click(event) {
    if (!this.contains(event.target)) {
      this.#closeMenus();
      return;
    }

    const trigger = event.target.closest('[data-vc-trigger]');
    if (trigger) {
      const field = trigger.closest('[data-vc-select]');
      const menu = field?.querySelector('.vc__menu');
      if (!menu) return;

      const open = menu.hidden;
      this.#closeMenus(field);
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      return;
    }

    const toggle = event.target.closest('[data-vc-toggle]');
    if (toggle) {
      const card = toggle.closest('.vc__card');
      const open = card.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const option = event.target.closest('[data-vc-option]');
    if (!option || !this.#root.contains(option)) return;

    const index = Number(option.dataset.vcIndex);
    if (option.dataset.vcPair) {
      this.#native(Number(this.#root.dataset.primaryIndex), option.dataset.vcPair);
    }
    this.#native(index, option.dataset.vcValue);
    this.#closeMenus();
  }

  #closeMenus(except) {
    this.#root?.querySelectorAll('[data-vc-select]').forEach((field) => {
      if (field === except) return;
      field.querySelector('[data-vc-trigger]')?.setAttribute('aria-expanded', 'false');
      const menu = field.querySelector('.vc__menu');
      if (menu) menu.hidden = true;
    });
  }

  #productSelect(event) {
    if (!this.#root) return;

    const normalize = (id) => String(id || '').split('/').pop();
    const eventProductId = event.product?.id;
    if (eventProductId && normalize(eventProductId) !== normalize(this.#root.dataset.productId)) return;

    const generation = ++this.#generation;
    Promise.resolve(event.promise)
      .then(({ variant } = {}) => {
        if (generation !== this.#generation || !this.isConnected) return;

        const resultProductId = variant?.product?.id || variant?.productId;
        if (resultProductId && normalize(resultProductId) !== normalize(this.#root.dataset.productId)) return;
        if (variant?.selectedOptions?.length) {
          this.#root.dataset.vcSelection = JSON.stringify(
            variant.selectedOptions.map((option) => option.value),
          );
        }
        this.#refresh(variant);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[gn-block-variant-picker] selection failed', error);
        }
      });
  }
}

if (!customElements.get('gn-block-variant-picker')) {
  customElements.define('gn-block-variant-picker', GnBlockVariantPicker);
}
